import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import Icon from '../../../components/AppIcon';
import { useLanguageProfile } from '../../../contexts/LanguageProfileContext';
import { platformAnalyticsService } from '../../../services/platformAnalyticsService';

const DATE_RANGES = [
  { label: '7D', value: 7 },
  { label: '30D', value: 30 },
  { label: '90D', value: 90 },
];

const PLATFORMS = [
  { key: 'wordpress', label: 'WordPress', color: '#21759B' },
  { key: 'pinterest', label: 'Pinterest', color: '#E60023' },
  { key: 'facebook', label: 'Facebook', color: '#1877F2' },
  { key: 'instagram', label: 'Instagram', color: '#C13584' },
  { key: 'threads', label: 'Threads', color: '#111827' },
  { key: 'etsy', label: 'Etsy', color: '#F56400' },
];

const SERIES = {
  wordpress: [
    { key: 'completed', label: 'Completed', color: '#21759B' },
    { key: 'published', label: 'Published', color: '#10B981' },
    { key: 'failed', label: 'Failed', color: '#EF4444' },
  ],
  pinterest: [
    { key: 'published', label: 'Published', color: '#10B981' },
    { key: 'scheduled', label: 'Scheduled', color: '#3B82F6' },
    { key: 'failed', label: 'Failed', color: '#EF4444' },
  ],
  facebook: [
    { key: 'published', label: 'Published', color: '#10B981' },
    { key: 'scheduled', label: 'Scheduled', color: '#3B82F6' },
    { key: 'failed', label: 'Failed', color: '#EF4444' },
  ],
  instagram: [
    { key: 'published', label: 'Published', color: '#10B981' },
    { key: 'scheduled', label: 'Scheduled', color: '#3B82F6' },
    { key: 'failed', label: 'Failed', color: '#EF4444' },
  ],
  threads: [
    { key: 'published', label: 'Published', color: '#10B981' },
    { key: 'scheduled', label: 'Scheduled', color: '#3B82F6' },
    { key: 'failed', label: 'Failed', color: '#EF4444' },
  ],
  etsy: [
    { key: 'synchronized', label: 'Listings synced', color: '#F56400' },
    { key: 'order', label: 'Orders', color: '#10B981' },
    { key: 'failed', label: 'Failed syncs', color: '#EF4444' },
  ],
};

const dateLabel = (value) => new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', timeZone: 'UTC',
});

const buildChartData = (days, rows, series) => {
  const byDate = new Map();
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date();
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCDate(date.getUTCDate() - offset);
    const key = date.toISOString().slice(0, 10);
    byDate.set(key, {
      date: key,
      label: dateLabel(key),
      ...Object.fromEntries(series.map((item) => [item.key, 0])),
    });
  }
  for (const row of rows) {
    const bucket = byDate.get(row.date);
    if (bucket && Object.prototype.hasOwnProperty.call(bucket, row.eventType)) {
      bucket[row.eventType] += row.total;
    }
  }
  return [...byDate.values()];
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-lg p-3 text-xs min-w-[150px]">
      <p className="font-semibold text-[#0F172A] mb-2">{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center justify-between gap-5 py-0.5">
          <span className="text-[#64748B]">{entry.name}</span>
          <span className="font-semibold text-[#0F172A]">{Number(entry.value).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const ActivityChart = () => {
  const { activeProfile } = useLanguageProfile();
  const requestId = useRef(0);
  const [days, setDays] = useState(7);
  const [platform, setPlatform] = useState('pinterest');
  const [accountId, setAccountId] = useState('all');
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    setAccountId('all');
  }, [platform, activeProfile?.id]);

  useEffect(() => {
    const current = ++requestId.current;
    setRows([]);
    setAccounts([]);
    setError(null);

    if (!activeProfile?.id) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    Promise.all([
      platformAnalyticsService.getAccounts(activeProfile.id, platform),
      platformAnalyticsService.getActivity({
        profileId: activeProfile.id,
        platform,
        accountId: accountId === 'all' ? null : accountId,
        days,
      }),
    ])
      .then(([nextAccounts, nextRows]) => {
        if (current !== requestId.current) return;
        setAccounts(nextAccounts);
        setRows(nextRows);
      })
      .catch((loadError) => {
        if (current !== requestId.current) return;
        setError(loadError.message || 'Unable to load automation activity.');
      })
      .finally(() => {
        if (current === requestId.current) setLoading(false);
      });

    return () => { requestId.current += 1; };
  }, [activeProfile?.id, accountId, days, platform, retryKey]);

  const series = SERIES[platform];
  const data = useMemo(() => buildChartData(days, rows, series), [days, rows, series]);
  const hasActivity = rows.some((row) => row.total > 0);
  const tickInterval = days <= 7 ? 0 : days <= 30 ? 4 : 13;

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5 sm:p-6 h-full flex flex-col">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-[#0F172A]">Automation Activity</h3>
          <p className="text-xs text-[#64748B] mt-0.5">Durable daily publishing and synchronization history</p>
        </div>
        <div className="flex items-center bg-[#F8FAFC] rounded-lg p-0.5 border border-[#E5E7EB] gap-0.5 self-start">
          {DATE_RANGES.map((range) => (
            <button
              key={range.value}
              type="button"
              onClick={() => setDays(range.value)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${days === range.value
                ? 'bg-white text-[#0F172A] shadow-sm border border-[#E5E7EB]'
                : 'text-[#64748B] hover:text-[#0F172A]'}`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {PLATFORMS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setPlatform(item.key)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all border"
            style={platform === item.key
              ? { backgroundColor: `${item.color}12`, borderColor: `${item.color}40`, color: item.color }
              : { backgroundColor: 'transparent', borderColor: '#E5E7EB', color: '#94A3B8' }}
          >
            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: platform === item.key ? item.color : '#CBD5E1' }} />
            {item.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <label htmlFor="activity-account" className="text-xs font-medium text-[#64748B]">Account</label>
        <select
          id="activity-account"
          value={accountId}
          onChange={(event) => setAccountId(event.target.value)}
          className="min-w-0 max-w-[260px] rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-xs text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-100"
        >
          <option value="all">All accounts</option>
          {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
        </select>
      </div>

      <div className="flex-1 min-h-0" style={{ minHeight: 210 }}>
        {loading ? (
          <div className="h-full flex items-center justify-center" role="status" aria-label="Loading activity">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#21759B]" />
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <Icon name="AlertCircle" size={28} className="text-red-400 mb-2" />
            <p className="text-sm font-medium text-[#0F172A]">Activity could not be loaded</p>
            <p className="text-xs text-[#64748B] mt-1 max-w-md">{error}</p>
            <button type="button" onClick={() => setRetryKey((value) => value + 1)} className="mt-3 text-xs font-semibold text-blue-600 hover:text-blue-700">Try again</button>
          </div>
        ) : !hasActivity ? (
          <div className="h-full flex flex-col items-center justify-center text-[#94A3B8] gap-2 text-center">
            <Icon name={accounts.length ? 'BarChart2' : 'Link2Off'} size={28} className="opacity-40" />
            <p className="text-sm">{accounts.length ? 'No activity in this period' : 'No connected accounts for this profile'}</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 0, right: 0, left: -20, bottom: 0 }} barGap={2}>
              <CartesianGrid strokeDasharray="2 4" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} interval={tickInterval} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: '#F8FAFC' }} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#64748B' }} />
              {series.map((item) => (
                <Bar key={item.key} dataKey={item.key} name={item.label} fill={item.color} radius={[3, 3, 0, 0]} maxBarSize={22} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ActivityChart;
